import { getLegacyBridge } from "./state";

type BeforeInstallPromptChoice = {
  outcome: "accepted" | "dismissed";
  platform: string;
};

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<BeforeInstallPromptChoice>;
};

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
const PWA_INSTALLED_STORAGE_KEY = "agentra-image-pwa-installed";

function isStandalone(): boolean {
  return window.matchMedia?.("(display-mode: standalone)")?.matches
    || Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
}

function isMarkedInstalled(): boolean {
  try {
    return window.localStorage.getItem(PWA_INSTALLED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function markInstalled(): void {
  try {
    window.localStorage.setItem(PWA_INSTALLED_STORAGE_KEY, "1");
  } catch {
    // Storage may be blocked; display-mode checks still hide inside the installed app.
  }
}

async function hasInstalledRelatedApp(): Promise<boolean> {
  const navigatorWithApps = window.navigator as Navigator & {
    getInstalledRelatedApps?: () => Promise<Array<{ platform?: string; url?: string }>>;
  };
  if (typeof navigatorWithApps.getInstalledRelatedApps !== "function") return false;
  try {
    const apps = await navigatorWithApps.getInstalledRelatedApps();
    return apps.some((app) => String(app.url || "").includes(window.location.origin));
  } catch {
    return false;
  }
}

function isIosLike(): boolean {
  const platform = window.navigator.platform || "";
  const userAgent = window.navigator.userAgent || "";
  const touchMac = platform === "MacIntel" && window.navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(userAgent) || touchMac;
}

function installButton(): HTMLButtonElement | null {
  return getLegacyBridge().els.installAppButton as HTMLButtonElement | null;
}

async function showInstallButton(): Promise<void> {
  if (isStandalone()) {
    markInstalled();
    hideInstallButton();
    return;
  }
  if (isMarkedInstalled() || await hasInstalledRelatedApp()) {
    hideInstallButton();
    return;
  }
  installButton()?.classList.remove("hidden");
}

function hideInstallButton(): void {
  installButton()?.classList.add("hidden");
}

async function registerServiceWorker(): Promise<void> {
  if (!("serviceWorker" in navigator) || !window.isSecureContext) return;
  try {
    await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
  } catch (error) {
    console.warn("PWA service worker registration failed", error);
  }
}

async function handleInstallClick(): Promise<void> {
  if (isStandalone() || isMarkedInstalled()) {
    hideInstallButton();
    return;
  }
  const promptEvent = deferredInstallPrompt;
  if (promptEvent) {
    deferredInstallPrompt = null;
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice.catch(() => null);
    if (choice?.outcome === "accepted") {
      markInstalled();
      hideInstallButton();
    }
    return;
  }
  if (isIosLike()) {
    window.alert("Trên iPhone/iPad: bấm nút Chia sẻ, rồi chọn Thêm vào Màn hình chính để cài AGENTRA IMAGE.");
  }
}

export function initPwaFeature(): void {
  if (isStandalone()) {
    markInstalled();
    hideInstallButton();
    return;
  }
  void registerServiceWorker();
  window.addEventListener("beforeinstallprompt", (event: Event) => {
    event.preventDefault();
    deferredInstallPrompt = event as BeforeInstallPromptEvent;
    void showInstallButton();
  });
  window.addEventListener("appinstalled", () => {
    markInstalled();
    hideInstallButton();
  });
  installButton()?.addEventListener("click", () => void handleInstallClick());
  if (isIosLike()) void showInstallButton();
}
